/**
 * Time stretch and pitch shift for library loops, using Signalsmith Stretch (MIT, WASM/AudioWorklet).
 *
 * Each loop that needs it is rendered offline once per (loop, tempo ratio, pitch) and cached in the
 * shared BufferCache under its own key, so playback and MP3 export play the exact same audio.
 * Loops are rendered with Signalsmith's input looping and the second cycle is kept, so the result is a
 * seamless loop (no start-up transient, no click at the wrap).
 */
import { deleteBuffer, getBuffer, getCached, putBuffer } from './BufferCache';
import { reverseClipBuffer } from './clipEdits';
import { isRenderedClip } from './clipKinds';
import { stretchRate } from './clipTiming';
import { resolveAudioUrl } from '@/api/loops';
import { isAudioEligible } from './classAudio';

export const PITCH_CHOICES = [-12, 0, 12];

/** Semitones for a library loop clip (-12..12, whole numbers). Rendered clips never pitch here. */
export function clipPitch(clip) {
  if (!clip || isRenderedClip(clip)) return 0;
  const value = Math.round(Number(clip.pitch) || 0);
  return Math.max(-12, Math.min(12, value));
}

/** BufferCache key for the audio a clip should play at this song tempo. */
export function bufferKeyForClip(clip, sessionBpm) {
  if (!clip) return null;
  if (isRenderedClip(clip)) return clip.loop_id;
  const rate = stretchRate(clip, sessionBpm);
  const pitch = clipPitch(clip);
  if (Math.abs(rate - 1) < 1e-6 && pitch === 0) return clip.loop_id;
  return `${clip.loop_id}|r${rate.toFixed(5)}|p${pitch}`;
}

/** The buffer to schedule for a clip: its stretched/pitched version when ready, else the original. */
export function playableBuffer(clip, sessionBpm, track = null) {
  if (track && !isAudioEligible(track)) return null;
  return reverseClipBuffer(getCached(bufferKeyForClip(clip, sessionBpm)) || getCached(clip?.loop_id) || null, clip, sessionBpm);
}

// Signalsmith builds its AudioWorklet from its own function source. The production minifier renames
// helpers that source depends on, so the worklet loads the untouched library file from public/vendor.
export const STRETCH_WORKLET_URL = `${process.env.PUBLIC_URL || ''}/vendor/signalsmith-stretch-1.3.2.mjs`;
const ENGINE_START_MS = 8000;
const RENDER_MS = 30000;

let stretchFactory = null;
let engineBroken = null; // set once the engine fails to start, so later plays skip straight to the original audio
async function loadStretch() {
  if (!stretchFactory) {
    const module = await import('signalsmith-stretch');
    stretchFactory = module.default || module.SignalsmithStretch || module;
    stretchFactory.moduleUrl = STRETCH_WORKLET_URL;
  }
  return stretchFactory;
}
/** Test seam: swap the Signalsmith factory. */
export function __setStretchFactory(factory) { stretchFactory = factory; engineBroken = null; }

function withTimeout(promise, ms, label) {
  let timer;
  const timeout = new Promise((_, reject) => { timer = setTimeout(() => reject(new Error(`${label} timed out`)), ms); });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

function offlineContext(channels, length, sampleRate) {
  const Context = globalThis.OfflineAudioContext || globalThis.webkitOfflineAudioContext;
  if (!Context) throw new Error('OfflineAudioContext is not supported by this browser');
  return new Context(channels, length, sampleRate);
}

const BLOCK = 32; // envelope resolution for timing alignment (~0.7 ms at 44.1 kHz)

function envelope(read, length, from, blocks, step = BLOCK) {
  const env = new Float32Array(blocks);
  for (let b = 0; b < blocks; b += 1) {
    let sum = 0;
    for (let i = 0; i < step; i += 1) sum += Math.abs(read(from + b * step + i));
    env[b] = sum / step;
  }
  return env;
}

/**
 * Offset (samples) between where the stretcher actually put the audio and where it belongs, found by
 * matching loudness envelopes against the source mapped through the tempo ratio. Transient-heavy loops
 * (drums) get locked to the grid; smooth pads have no clear peak and stay at 0.
 */
export function estimateLag(rendered, source, rate, cycle, maxLag) {
  const mix = (buffer, index) => {
    let sum = 0; for (let c = 0; c < buffer.numberOfChannels; c += 1) sum += buffer.getChannelData(c)[index] || 0;
    return sum;
  };
  const srcLength = source.length;
  const blocks = Math.floor(cycle / BLOCK);
  const maxBlocks = Math.floor(maxLag / BLOCK);
  // Reference: the source as it should sound at output time t (input time t * rate, wrapping like the loop).
  const ref = new Float32Array(blocks);
  for (let b = 0; b < blocks; b += 1) {
    let sum = 0; const count = Math.max(1, Math.round(BLOCK * rate));
    for (let i = 0; i < count; i += 1) sum += Math.abs(mix(source, Math.floor((b * BLOCK * rate + i)) % srcLength));
    ref[b] = sum / count;
  }
  const out = envelope((index) => mix(rendered, index), rendered.length, cycle - maxBlocks * BLOCK, blocks + maxBlocks * 2);
  const score = (lagBlocks) => { let total = 0; for (let b = 0; b < blocks; b += 1) total += ref[b] * out[b + maxBlocks + lagBlocks]; return total; };
  const zero = score(0);
  let best = 0; let bestScore = zero;
  for (let lag = -maxBlocks; lag <= maxBlocks; lag += 1) { const value = score(lag); if (value > bestScore) { bestScore = value; best = lag; } }
  return bestScore > zero * 1.05 ? best * BLOCK : 0;
}

/**
 * Render one seamless cycle of `source` at `rate` (1 = original speed) and `semitones`.
 * Output length = source length / rate. Signalsmith loops the input; we keep the warmed-up second cycle,
 * shift it so hits land on the grid, and crossfade the wrap so the loop repeats with no tick.
 */
export async function renderStretchedLoop(source, { rate = 1, semitones = 0 } = {}) {
  const channels = Math.max(1, source.numberOfChannels);
  const sampleRate = source.sampleRate;
  const cycle = Math.max(1, Math.round(source.length / rate));
  const fade = Math.min(Math.round(sampleRate * 0.012), Math.floor(cycle / 4));
  const maxLag = Math.min(Math.round(sampleRate * 0.012), Math.floor(cycle / 4));
  const ctx = offlineContext(channels, cycle * 2 + maxLag + fade, sampleRate);
  if (engineBroken) throw engineBroken;
  const factory = await loadStretch();
  let node;
  try {
    node = await withTimeout(factory(ctx, { numberOfInputs: 0, numberOfOutputs: 1, outputChannelCount: [channels] }), ENGINE_START_MS, 'Stretch engine start');
  } catch (error) {
    engineBroken = error;
    throw error;
  }
  const input = Array.from({ length: channels }, (_, index) => source.getChannelData(index).slice());
  await withTimeout(node.addBuffers(input), ENGINE_START_MS, 'Stretch engine load');
  node.schedule({ output: 0, input: 0, rate, semitones, loopStart: 0, loopEnd: source.duration, active: true });
  node.connect(ctx.destination);
  const rendered = await withTimeout(ctx.startRendering(), RENDER_MS, 'Stretch render');
  const start = cycle + estimateLag(rendered, source, rate, cycle, maxLag);
  const out = typeof AudioBuffer === 'function'
    ? new AudioBuffer({ length: cycle, numberOfChannels: channels, sampleRate })
    : ctx.createBuffer(channels, cycle, sampleRate);
  for (let index = 0; index < channels; index += 1) {
    const data = rendered.getChannelData(index);
    const segment = data.slice(start, start + cycle);
    // The loop's end flows into what the stretcher played next (data[start + cycle + i]); fade from that
    // continuation into the loop start so the wrap is continuous.
    for (let i = 0; i < fade; i += 1) {
      const w = 0.5 - 0.5 * Math.cos((Math.PI * i) / fade);
      segment[i] = (data[start + cycle + i] || 0) * (1 - w) + segment[i] * w;
    }
    out.copyToChannel(segment, index);
  }
  return out;
}

const inFlight = new Map();
const renderedKeys = new Set();
let queue = Promise.resolve();

/**
 * Make sure every library loop that needs stretching or pitch shifting has its rendered buffer.
 * Renders one at a time (they are CPU heavy), dedupes identical requests, never throws for one bad loop.
 */
export async function ensureStretchedBuffers(tracks, sessionBpm) {
  const jobs = new Map();
  const needed = new Set();
  for (const track of tracks || []) for (const clip of isAudioEligible(track) ? (track.clips || []) : []) {
    if (isRenderedClip(clip)) continue;
    const key = bufferKeyForClip(clip, sessionBpm);
    if (key) needed.add(key);
    if (!key || key === clip.loop_id || getCached(key) || jobs.has(key)) continue;
    jobs.set(key, { clip, rate: stretchRate(clip, sessionBpm), semitones: clipPitch(clip) });
  }
  // Free stretched/pitched copies no clip needs any more (old tempos, removed clips): ~7 MB per 8-bar loop.
  for (const key of [...renderedKeys]) if (!needed.has(key) && !inFlight.has(key)) { deleteBuffer(key); renderedKeys.delete(key); }
  await Promise.all([...jobs].map(([key, job]) => {
    if (inFlight.has(key)) return inFlight.get(key);
    const run = queue.then(async () => {
      if (getCached(key)) return getCached(key);
      const base = getCached(job.clip.loop_id) || await getBuffer(job.clip.loop_id, resolveAudioUrl(job.clip.loop_id, job.clip));
      const buffer = await renderStretchedLoop(base, { rate: job.rate, semitones: job.semitones });
      putBuffer(key, buffer); renderedKeys.add(key);
      return buffer;
    });
    queue = run.catch(() => null); // one failed render never blocks the ones queued after it
    const promise = run.catch((error) => {
      console.warn(`Stretch render failed for ${job.clip.loop_id}:`, error?.message || error);
      return null;
    }).finally(() => inFlight.delete(key));
    inFlight.set(key, promise);
    return promise;
  }));
}

// Development-only debugging hook (never in production builds): lets the browser console audition renders.
if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') window.__baStretch = { renderStretchedLoop };
